// Lead Generation System — the manager's real-time compensation-plan editor.
// Per-KPI dollar rates, per-bucket + total caps (day/week/month), per-KPI daily
// count limits, quality-multiplier bands, and grade weights — for the program
// default plan or a per-generator override. Saving writes a NEW effective-dated
// version: it applies to NEW events only and never rewrites a locked week.
// Reads gate on LeadGen.View; saving gates on LeadGen.ManagePlan.

import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  SubmitButton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useDateFormatter } from "@/utils/datetime";
import { calendarDateInputToIso } from "@/design-system/lib/format";
import type { LeadGenPlanConfig } from "@/api/leadGen/compensationPlan";
import { useLeadGenerators } from "@/hooks/leadGen/useLeadGenerators";
import {
  useEffectiveConfig,
  useGeneratorPlan,
  useProgramDefaultPlan,
  useRevertPlan,
  useSavePlan,
} from "@/hooks/leadGen/useCompensationPlan";
import { EMPTY_GUID, PLAN_FIELD_GROUPS, type PlanFieldKind } from "./leadGenShared";

const MANAGE_PERMISSION = "LeadGen.ManagePlan";

type Draft = Record<string, string>;

function configToDraft(config: LeadGenPlanConfig): Draft {
  const draft: Draft = {};
  for (const group of PLAN_FIELD_GROUPS) {
    for (const f of group.fields) {
      const v = config[f.key];
      draft[f.key] = v === null || v === undefined ? "" : String(v);
    }
  }
  return draft;
}

function draftToConfig(draft: Draft): { config: LeadGenPlanConfig | null; invalid: string[] } {
  const out: Record<string, number | null> = {};
  const invalid: string[] = [];
  for (const group of PLAN_FIELD_GROUPS) {
    for (const f of group.fields) {
      const raw = (draft[f.key] ?? "").trim();
      if (raw === "") {
        if (f.nullable) out[f.key] = null;
        else invalid.push(f.label);
        continue;
      }
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) {
        invalid.push(f.label);
        continue;
      }
      out[f.key] = n;
    }
  }
  return { config: invalid.length ? null : (out as unknown as LeadGenPlanConfig), invalid };
}

/** True when the current editor value for a field differs from the program
 *  default. Blank/unset is treated identically on both sides (an unset cap on
 *  both is NOT a diff); set values compare numerically so "5.00" vs 5 is equal. */
function fieldDiffers(draftRaw: string | undefined, defaultVal: number | null | undefined): boolean {
  const raw = (draftRaw ?? "").trim();
  const draftUnset = raw === "";
  const defaultUnset = defaultVal === null || defaultVal === undefined;
  if (draftUnset && defaultUnset) return false;
  if (draftUnset !== defaultUnset) return true;
  return Number(raw) !== defaultVal;
}

export default function CompensationPlanSettingsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(MANAGE_PERMISSION);
  const toast = useToast();
  const dialogs = useDialogs();
  const { date } = useDateFormatter();

  // "" = the program-default scope; a GUID = a per-generator override.
  const [generatorScope, setGeneratorScope] = useState("");
  const scopeId = generatorScope || EMPTY_GUID;

  const generators = useLeadGenerators({ page: 1, pageSize: 200 });
  const effective = useEffectiveConfig(scopeId);
  // The program-default effective config — the diff baseline in override scope.
  // In program scope this resolves the SAME key as `effective` (deduped).
  const programDefaultConfig = useEffectiveConfig(EMPTY_GUID);
  const programPlan = useProgramDefaultPlan();
  const generatorPlan = useGeneratorPlan(generatorScope || null);
  const save = useSavePlan();
  const revert = useRevertPlan();

  const currentVersion = generatorScope ? generatorPlan.data : programPlan.data;

  const [draft, setDraft] = useState<Draft>({});
  const [notes, setNotes] = useState("");
  const [effDate, setEffDate] = useState("");

  useEffect(() => {
    if (effective.data) setDraft(configToDraft(effective.data));
    // Re-seed whenever the scope's effective config resolves (scope change or
    // post-save refetch), mirroring the card-surcharge grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective.data, scopeId]);

  const setField = (key: string, value: string) => setDraft((d) => ({ ...d, [key]: value }));

  // Fields whose current editor value differs from the current program default —
  // only meaningful in an override scope. Empty (nothing highlighted) in the
  // program-default scope or while the baseline read is still loading.
  const diffKeys = useMemo(() => {
    const set = new Set<string>();
    const baseline = programDefaultConfig.data;
    if (!generatorScope || !baseline) return set;
    for (const group of PLAN_FIELD_GROUPS) {
      for (const f of group.fields) {
        if (fieldDiffers(draft[f.key], baseline[f.key])) set.add(f.key);
      }
    }
    return set;
  }, [generatorScope, programDefaultConfig.data, draft]);

  const onRevert = async () => {
    if (!generatorScope) return;
    const ok = await dialogs.confirm({
      title: "Revert to program default",
      message:
        "Remove this generator's override so the program default plan applies to new events? Their custom plan will be discarded.",
      confirmLabel: "Revert",
    });
    if (!ok) return;
    revert.mutate(generatorScope, {
      onSuccess: () => toast.success("Reverted to program default"),
      onError: (err) => toast.error(extractErrorMessage(err)),
    });
  };

  const onSave = () => {
    const { config, invalid } = draftToConfig(draft);
    if (!config) {
      toast.error(`Fix required values: ${invalid.join(", ")}`);
      return;
    }
    save.mutate(
      {
        leadGeneratorId: generatorScope || null,
        config,
        notes: notes.trim() || null,
        effectiveFrom: effDate ? calendarDateInputToIso(effDate) : null,
      },
      {
        onSuccess: () => {
          toast.success("New plan version saved");
          setNotes("");
          setEffDate("");
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  if (!canManage) {
    return (
      <div className="flex flex-col gap-space-4">
        <PageHeader eyebrow="Lead Generation" title="Compensation plan" />
        <EmptyState
          title="Not authorized"
          description="You need LeadGen.ManagePlan to edit the lead-generation compensation plan."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-5">
      <PageHeader
        eyebrow="Lead Generation"
        title="Compensation plan"
        description="Edit the per-KPI rates, caps, daily count limits, multiplier bands, and grade weights. DISPLAY-ONLY — the plan feeds the standalone lead-gen ledger; no money moves in the system. Saving writes a new effective-dated version that applies to NEW events only and never rewrites a locked week."
      />

      <div className="flex flex-wrap items-end justify-between gap-space-3">
        <label className="flex flex-col gap-space-1">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Scope</span>
          <select
            className="h-10 w-72 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
            value={generatorScope}
            onChange={(e) => setGeneratorScope(e.target.value)}
          >
            <option value="">Program default (all generators)</option>
            {(generators.data?.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                Override · {g.displayName}
                {g.hasCustomPlan ? " • custom" : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-end gap-space-3">
          <Card className="min-w-[260px]">
            <div className="text-caption uppercase tracking-wide text-gray-mid">Current version</div>
            {currentVersion ? (
              <div className="text-small text-onyx">
                v{currentVersion.version} · effective {date(currentVersion.effectiveFrom)}
                {currentVersion.notes ? <div className="text-caption text-gray-mid">{currentVersion.notes}</div> : null}
              </div>
            ) : (
              <div className="text-small text-gray-mid">
                {generatorScope
                  ? "No override — the program default applies (built-in defaults if none saved)."
                  : "No saved version — built-in program defaults apply."}
              </div>
            )}
          </Card>
          {generatorScope && generatorPlan.data ? (
            <Button variant="secondary" type="button" onClick={onRevert} disabled={revert.isPending}>
              {revert.isPending ? "Reverting…" : "Revert to program default"}
            </Button>
          ) : null}
        </div>
      </div>

      {effective.isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : effective.isError ? (
        <p className="text-small text-status-error" role="alert">
          {extractErrorMessage(effective.error)}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
            {PLAN_FIELD_GROUPS.map((group) => (
              <Card key={group.title}>
                <h3 className="font-display text-h3 text-onyx m-0">{group.title}</h3>
                <p className="text-caption text-gray-mid mt-space-1 mb-space-3">{group.help}</p>
                <Table>
                  <THead>
                    <TR>
                      <TH>Setting</TH>
                      <TH className="text-right">Value</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {group.fields.map((f) => (
                      <TR key={f.key}>
                        <TD className="text-small text-onyx">
                          <span className="inline-flex items-center gap-space-2">
                            {f.label}
                            {diffKeys.has(f.key) && <Badge status="gold">Custom</Badge>}
                          </span>
                        </TD>
                        <TD className="text-right">
                          <PlanInput
                            kind={f.kind}
                            nullable={f.nullable}
                            value={draft[f.key] ?? ""}
                            onChange={(v) => setField(f.key, v)}
                            label={f.label}
                          />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </Card>
            ))}
          </div>

          <Card featured>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-4">
              <label className="flex flex-col gap-space-1">
                <span className="text-caption uppercase tracking-wide text-gray-mid">Version notes (optional)</span>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </label>
              <label className="flex flex-col gap-space-1">
                <span className="text-caption uppercase tracking-wide text-gray-mid">Effective from (optional)</span>
                <Input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} className="w-48" />
                <span className="text-caption text-gray-mid">
                  Blank = now. Must be after the current version's effective date. Existing and locked weeks are never rewritten.
                </span>
              </label>
            </div>
            <div className="mt-space-4 flex justify-end">
              <SubmitButton type="button" pending={save.isPending} pendingLabel="Saving…" onClick={onSave}>
                Save new version
              </SubmitButton>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function PlanInput({
  kind,
  nullable,
  value,
  onChange,
  label,
}: {
  kind: PlanFieldKind;
  nullable: boolean;
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  const step = kind === "count" || kind === "weight" ? "1" : "0.01";
  const prefix = kind === "dollar" ? "$" : null;
  const suffix = kind === "multiplier" ? "×" : null;
  return (
    <div className="inline-flex items-center justify-end gap-space-1">
      {prefix && <span className="text-gray-mid text-small">{prefix}</span>}
      <Input
        type="number"
        min={0}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={nullable ? "—" : "0"}
        aria-label={label}
        className="w-28 text-right tabular-nums"
      />
      {suffix && <span className="text-gray-mid text-small">{suffix}</span>}
    </div>
  );
}
